import React, { useEffect, useState, useCallback } from 'react';
import { getDepletionReadiness } from '../lib/api';
import { Activity, ShieldAlert, ShieldCheck, AlertCircle, Info, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../lib/utils';
import logger from '../lib/logger';

// "Why not active?" panel — surfaces depletion readiness with full transparency.
// Doctrine (May 7 2026, Slice 2):
//   Coverage  = "Can we MAP sales?"
//   Readiness = "Can we TRUST automated depletion?"
// Operators see:
//   - both KPIs (Coverage % / Readiness %) with thresholds
//   - per-axis breakdown (mapping / structural / costing)
//   - blockers + soft degraders sorted by qty impact
//   - actionable why-not-active items
//   - full formula explainer (we don't hide the math)

const Tone = {
  ok: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  warn: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  bad: 'bg-red-500/10 text-red-300 border-red-500/30',
  zinc: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/30',
};

const KpiTile = ({ label, value, threshold, ok, suffix = '%', testId }) => (
  <div
    className={cn('px-4 py-3 rounded-lg border', ok === undefined ? Tone.zinc : ok ? Tone.ok : Tone.bad)}
    data-testid={testId}
  >
    <p className="text-[10px] uppercase tracking-widest opacity-70 font-medium">{label}</p>
    <p className="text-2xl font-mono font-bold mt-1">
      {(value === null || value === undefined) ? '—' : `${value.toFixed(1)}${suffix}`}
    </p>
    {threshold !== undefined && threshold !== null && (
      <p className="text-[10px] opacity-60 mt-0.5 font-mono">
        threshold: {threshold}{suffix}
      </p>
    )}
  </div>
);

const AxisBar = ({ label, score, testId }) => {
  const tone =
    score >= 80 ? 'bg-emerald-500' :
    score >= 50 ? 'bg-amber-500' :
                  'bg-red-500';
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-zinc-400 font-medium">{label}</span>
        <span className="text-xs font-mono text-zinc-200">{score.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 bg-[#27272a] rounded-full overflow-hidden">
        <div className={cn('h-full transition-all', tone)} style={{ width: `${Math.min(100, Math.max(0, score))}%` }} />
      </div>
    </div>
  );
};

const ActionItem = ({ a }) => {
  const isSoft = a.severity === 'soft';
  return (
    <li className="flex items-start gap-2 text-xs">
      <span
        className={cn(
          'mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0',
          isSoft ? 'bg-amber-500' : 'bg-red-500'
        )}
      />
      <span className="text-zinc-300">{a.label}</span>
    </li>
  );
};

const DepletionReadinessPanel = ({ outletId, refreshKey, defaultExpanded = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFormula, setShowFormula] = useState(false);
  const [showDetail, setShowDetail] = useState(defaultExpanded);

  const load = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getDepletionReadiness(outletId);
      setData(res);
    } catch (e) {
      logger.warn('[DepletionReadiness] fetch failed:', e);
      setError(e?.response?.data?.detail || e.message || 'Failed to load readiness');
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return (
      <div
        className="p-4 bg-[#18181b] border border-[#27272a] rounded-lg flex items-center gap-2 text-sm text-zinc-500"
        data-testid="readiness-loading"
      >
        <RefreshCw className="w-4 h-4 animate-spin" />
        Computing depletion readiness...
      </div>
    );
  }
  if (error || !data) {
    return (
      <div
        className="p-4 bg-[#18181b] border border-red-500/30 rounded-lg text-sm text-red-300 flex items-center gap-2"
        data-testid="readiness-error"
      >
        <AlertCircle className="w-4 h-4" />
        {typeof error === 'string' ? error : 'Could not load readiness.'}
      </div>
    );
  }

  const blocked = data.why_not_active?.blocked;
  const summaryIcon = blocked ? ShieldAlert : ShieldCheck;
  const Icon = summaryIcon;

  return (
    <div
      className={cn(
        'rounded-xl border overflow-hidden',
        blocked
          ? 'bg-red-500/5 border-red-500/30'
          : 'bg-emerald-500/5 border-emerald-500/30'
      )}
      data-testid="depletion-readiness-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 flex items-center gap-3">
        <Icon className={cn('w-5 h-5 flex-shrink-0', blocked ? 'text-red-400' : 'text-emerald-400')} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-zinc-100">
            {blocked ? 'Active mode is blocked' : 'Active mode requirements met'}
          </p>
          <p className="text-xs text-zinc-400 mt-0.5" data-testid="readiness-summary">
            {data.why_not_active?.summary}
          </p>
        </div>
        <button
          onClick={() => setShowDetail((s) => !s)}
          className="p-1.5 rounded hover:bg-[#27272a] transition-colors"
          data-testid="readiness-toggle"
          aria-label={showDetail ? 'Collapse details' : 'Expand details'}
        >
          {showDetail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <button
          onClick={load}
          className="p-1.5 rounded hover:bg-[#27272a] transition-colors"
          data-testid="readiness-refresh"
          aria-label="Refresh readiness"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {showDetail && (
        <div className="px-4 pb-4 space-y-4 border-t border-zinc-800">
          {/* Headline KPIs */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <KpiTile
              label="Coverage"
              value={data.coverage_pct}
              threshold={data.coverage_threshold}
              ok={data.why_not_active?.coverage_ok}
              testId="readiness-kpi-coverage"
            />
            <KpiTile
              label="Readiness"
              value={data.readiness_pct}
              threshold={data.readiness_threshold}
              ok={data.why_not_active?.readiness_ok}
              testId="readiness-kpi-readiness"
            />
          </div>

          {/* Per-axis bars */}
          <div className="space-y-3 bg-[#0a0a0a] rounded-lg p-3 border border-[#27272a]">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium flex items-center gap-1.5">
              <Activity className="w-3 h-3" />
              Per-axis (qty-weighted)
            </p>
            <AxisBar label="Mapping" score={data.axes?.mapping?.score || 0} testId="axis-mapping" />
            <AxisBar label="Structural" score={data.axes?.structural?.score || 0} testId="axis-structural" />
            <AxisBar label="Costing" score={data.axes?.costing?.score || 0} testId="axis-costing" />
          </div>

          {/* Why not active actions */}
          {(data.why_not_active?.actions || []).length > 0 && (
            <div className="bg-[#0a0a0a] rounded-lg p-3 border border-[#27272a]">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium mb-2">
                What unblocks activation
              </p>
              <ul className="space-y-2" data-testid="readiness-actions">
                {data.why_not_active.actions.map((a, i) => <ActionItem key={i} a={a} />)}
              </ul>
            </div>
          )}

          {/* Hard blockers */}
          {(data.blockers?.structurally_invalid_recipes || []).length > 0 && (
            <details className="bg-[#0a0a0a] rounded-lg border border-red-500/20" data-testid="hard-blockers">
              <summary className="px-3 py-2 text-xs font-medium text-red-300 cursor-pointer">
                {data.blockers.structurally_invalid_recipes.length} structurally invalid recipe(s) — hard blockers
              </summary>
              <div className="px-3 pb-3 space-y-2">
                {data.blockers.structurally_invalid_recipes.slice(0, 8).map((b, i) => (
                  <div key={i} className="text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-zinc-200 truncate">{b.recipe_name || b.recipe_id}</span>
                      <span className="font-mono text-zinc-500">qty={b.qty_impact}</span>
                    </div>
                    <ul className="ml-3 mt-1 space-y-0.5">
                      {(b.diagnostics || []).slice(0, 3).map((d, j) => (
                        <li key={j} className="text-zinc-400 flex items-start gap-1.5">
                          <span className="font-mono text-[9px] px-1 py-0.5 rounded bg-red-500/10 text-red-300 border border-red-500/20 flex-shrink-0">
                            {d.code}
                          </span>
                          <span>{d.message}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Soft degraders */}
          {(data.soft_degraders?.missing_cost_recipes?.length > 0 ||
            data.soft_degraders?.theoretical_only_rollups?.length > 0 ||
            data.soft_degraders?.stale_costing_recipes?.length > 0) && (
            <details className="bg-[#0a0a0a] rounded-lg border border-amber-500/20" data-testid="soft-degraders">
              <summary className="px-3 py-2 text-xs font-medium text-amber-300 cursor-pointer">
                Soft degraders (depletion still works; costing quality reduced)
              </summary>
              <div className="px-3 pb-3 space-y-1 text-xs">
                {data.soft_degraders.missing_cost_recipes?.slice(0, 5).map((r, i) => (
                  <div key={`mc-${i}`} className="flex items-center justify-between">
                    <span className="text-zinc-400 truncate">missing cost · {r.recipe_name}</span>
                    <span className="font-mono text-zinc-500">qty={r.qty_impact}</span>
                  </div>
                ))}
                {data.soft_degraders.theoretical_only_rollups?.slice(0, 5).map((r, i) => (
                  <div key={`th-${i}`} className="flex items-center justify-between">
                    <span className="text-zinc-400 truncate">theoretical-only · {r.recipe_name}</span>
                    <span className="font-mono text-zinc-500">qty={r.qty_impact}</span>
                  </div>
                ))}
                {data.soft_degraders.stale_costing_recipes?.slice(0, 5).map((r, i) => (
                  <div key={`st-${i}`} className="flex items-center justify-between">
                    <span className="text-zinc-400 truncate">stale cost · {r.recipe_name}</span>
                    <span className="font-mono text-zinc-500">qty={r.qty_impact}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Formula explainer (don't hide the math) */}
          <button
            onClick={() => setShowFormula((s) => !s)}
            className="w-full text-left flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-zinc-300"
            data-testid="readiness-formula-toggle"
          >
            <Info className="w-3 h-3" />
            {showFormula ? 'Hide' : 'How is this calculated?'}
          </button>
          {showFormula && (
            <div
              className="p-3 bg-[#0a0a0a] rounded-lg border border-[#27272a] text-[11px] text-zinc-400 leading-relaxed"
              data-testid="readiness-formula"
            >
              {data.formula_explainer}
              <div className="mt-2 pt-2 border-t border-zinc-800 font-mono">
                soft_credit = {data.soft_credit} · stale_days = {data.stale_days}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DepletionReadinessPanel;
